import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';

import statesData from './states';
import usePayers from '../../hooks/usePayers';
import { useModal } from '../../context/ModalContext';

export default function useCheckEligibility(resetInsuranceOnStateChange = true) {
  const form = useForm({
    defaultValues: { email: '', terms: false },
  });

  const { closeModal } = useModal();
  const payers = usePayers();
  const states = Object.entries(statesData).map(([code, name]) => ({ code, name }));

  const [inputState, setInputState] = useState('');
  const [isStateValid, setIsStateValid] = useState(false);
  const [inputInsurance, setInputInsurance] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [result, setResult] = useState(null);

  useEffect(() => {
    if (resetInsuranceOnStateChange) setInputInsurance('');

    setIsStateValid(false);
  }, [inputState]);

  const insuranceSelected = () => payers.find(
    (insurance) => insurance.displayName.toLowerCase() === inputInsurance.toLowerCase(),
  );
  const stateSelected = () => states.find(
    (state) => state.name.toLowerCase() === inputState.toLowerCase(),
  );
  const emailSubmitted = () => result === 'emailSubmitted';

  function displayPopup(text, status = null) {
    const popupElement = document.querySelector('.eligibility-modal>.popup');
    popupElement.querySelector('p').innerHTML = text;

    popupElement.classList.remove(...popupElement.classList);
    popupElement.classList.add('popup', 'active', status);

    setTimeout(() => {
      popupElement.classList.remove('active');
    }, 3000);
  }

  async function handleCheckState(value) {
    if (!value) return;

    const stateCode = Object.keys(statesData).find(
      (code) => statesData[code].toLowerCase() === value.toLowerCase(),
    );
    if (!stateCode) return;

    const check = await fetch(`${process.env.BOOKING_BASE_URL}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `{
          checkStateAvailability(state: "${stateCode}")
        }`,
      }),
    });
    const isValid = (await check.json()).data.checkStateAvailability;

    if (!isValid) {
      setResult('notEligibleState');
    } else {
      setIsStateValid(true);
    }
  }

  async function handleCheckEligibility() {
    const payer = payers.find(
      (i) => i.displayName.toLowerCase() === inputInsurance.toLowerCase(),
    );
    if (!payer.payer) return 'notEligibleInsurance';

    const stateCode = Object.keys(statesData).find(
      (code) => statesData[code].toLowerCase() === inputState.toLowerCase(),
    );
    if (!stateCode) return 'notEligibleState';

    setIsLoading(true);

    const check = await fetch(`${process.env.BOOKING_BASE_URL}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `{
          checkPayerAvailability(payer: "${payer.payer}", state: "${stateCode}")
        }`,
      }),
    });
    const isEligible = (await check.json()).data.checkPayerAvailability;
    const newResult = isEligible ? 'eligible' : 'notEligibleInsurance';

    setResult(newResult);

    if (window.posthog) {
      window.posthog.capture('eligibility_check_confirmed', {
        state: inputState,
        insurance: inputInsurance,
        is_eligible: isEligible,
      });
    }
    setIsLoading(false);

    return newResult;
  }

  function handleCloseButton() {
    closeModal();

    if (emailSubmitted()) {
      setResult(null);
      setInputState('');
      setInputInsurance('');
      setIsStateValid(false);
      form.reset();
    }
  }

  async function submitEmail(data) {
    if (form.formState.isSubmitting || emailSubmitted()) return;

    const response = await fetch(`${process.env.FORM_SERVICE_URL}/extra/store-lead/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: data.email,
        state: inputState,
        reason: 'state_unavailable',
      }),
    });

    if (response.status !== 200) {
      displayPopup('Email not sent, please try again', 'error');
    } else {
      setResult('emailSubmitted');
    }
  }

  return {
    form,
    handleCloseButton,
    result,
    emailSubmitted,
    setResult,
    inputState,
    setInputState,
    handleCheckState,
    states,
    isStateValid,
    inputInsurance,
    setInputInsurance,
    handleCheckEligibility,
    insurances: payers,
    stateSelected,
    insuranceSelected,
    submitEmail,
    isLoading,
  };
}
